import { inject, Injectable } from '@angular/core';
import {
  CanDeactivateFn,
  Event,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  Router
} from '@angular/router';
import { DataChangeWatcherService } from 'mbs-ui-kit';
import { defaultIfEmpty, filter, of, tap } from 'rxjs';

const isNavigationSettled = (event: Event): boolean =>
  event instanceof NavigationEnd ||
  event instanceof NavigationError ||
  (event instanceof NavigationCancel && event.code !== NavigationCancellationCode.Redirect);

// A redirect from the target route canActivate (e.g., /mail → /mail/<userId>) starts a new navigation that runs
// canDeactivate again; without remembering the confirmed leave, the user gets the unsaved changes modal twice
@Injectable({ providedIn: 'root' })
export class LeaveConfirmationService {
  public confirmedUrl: string = null;

  constructor() {
    inject(Router)
      .events.pipe(filter(isNavigationSettled))
      .subscribe(() => (this.confirmedUrl = null));
  }
}

export const dataChangeWatcherGuard: CanDeactivateFn<unknown> = (_component, _route, currentState) => {
  const leaveConfirmation = inject(LeaveConfirmationService);
  const dataChangeWatcher = inject(DataChangeWatcherService);

  if (leaveConfirmation.confirmedUrl === currentState.url) {
    return of(true);
  }

  // a sidepanel's failed save completes without a value; Angular would turn an empty guard into a NavigationError
  return dataChangeWatcher.detectChanges().pipe(
    defaultIfEmpty(false),
    tap((canLeave) => (leaveConfirmation.confirmedUrl = canLeave ? currentState.url : null))
  );
};
